import type { SupplierManufacturer } from "@/app/services/generated/suppliermanufacturerService";
import { SupplierManufacturerService } from "@/app/services/generated/suppliermanufacturerService";
import type BaseEnumType from "@/app/services/baseEnumType";
import type Guid from "@/app/types/common/guid";

export default class SupplierManufacturerServiceExtended extends SupplierManufacturerService {
  public async getAllSupplierManufacturersByBusinessType(
    tenantId: Guid,
    businessType: EnumBusinessType
  ): Promise<SupplierManufacturer[]> {
    const supplierManufacturers = await super.getAllSupplierManufacturers(
      tenantId
    );
    return supplierManufacturers.filter((supplierManufacturer) =>
      businessType.isBusinessTypeOf(supplierManufacturer)
    );
  }
}

export class EnumBusinessType implements BaseEnumType {
  private static allValues: EnumBusinessType[] = []; // make sure this is top-most

  public static none = new EnumBusinessType(
    0,
    "None",
    "enum_business_type_none",
    () => false
  );
  public static supplier = new EnumBusinessType(
    1,
    "Supplier",
    "enum_business_type_supplier",
    (supplierManufacturer: Readonly<SupplierManufacturer>) =>
      supplierManufacturer.isSupplier
  );
  public static manufacturer = new EnumBusinessType(
    2,
    "Manufacturer",
    "enum_business_type_manufacturer",
    (supplierManufacturer: Readonly<SupplierManufacturer>) =>
      supplierManufacturer.isManufacturer
  );
  public static supplierAndManufacturer = new EnumBusinessType(
    3,
    "SupplierAndManufacturer",
    "enum_business_type_supplier_and_manufacturer",
    (supplierManufacturer: Readonly<SupplierManufacturer>) =>
      supplierManufacturer.isSupplier || supplierManufacturer.isManufacturer
  );

  public key: number;
  public value: string;
  public translationKey?: string;
  private readonly supplierMatch: (
    supplierManufacturer: Readonly<SupplierManufacturer>
  ) => boolean;

  private constructor(
    key: number,
    value: string,
    translationKey: string,
    supplierMatch: (
      supplierManufacturer: Readonly<SupplierManufacturer>
    ) => boolean
  ) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    this.supplierMatch = supplierMatch;

    EnumBusinessType.allValues.push(this);
  }

  public static getValues(): EnumBusinessType[] {
    return EnumBusinessType.allValues;
  }

  public isBusinessTypeOf(
    supplierManufacturer: Readonly<SupplierManufacturer>
  ): boolean {
    return this.supplierMatch(supplierManufacturer);
  }

  public static getByKey(key: number): EnumBusinessType {
    const found = EnumBusinessType.allValues.find((e) => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumBusinessType - Key not found: ${key}`);
    }

    return found;
  }

  public includesSupplier() {
    return (
      this.key === EnumBusinessType.supplier.key ||
      this.key === EnumBusinessType.supplierAndManufacturer.key
    );
  }

  public includesManufacturer() {
    return (
      this.key === EnumBusinessType.manufacturer.key ||
      this.key === EnumBusinessType.supplierAndManufacturer.key
    );
  }

  public static fromBooleans(
    isSupplier: boolean,
    isManufacturer: boolean
  ): EnumBusinessType {
    if (isManufacturer) {
      return isSupplier
        ? EnumBusinessType.supplierAndManufacturer
        : EnumBusinessType.manufacturer;
    }
    return isSupplier ? EnumBusinessType.supplier : EnumBusinessType.none;
  }
}

export const supplierManufacturerService =
  new SupplierManufacturerServiceExtended();
